import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { DEFAULT_PERSONA_ID, getPersona, PERSONAS } from '../config/personas';

const STORAGE_KEY = 'hai-job.preferences.v1';

/** Default empty preferences, nothing skews the prompt until user fills it in. */
const DEFAULTS = {
  tone: '',           // 'formeel' | 'informeel' | 'jong' | 'zakelijk' | ''
  paragraphStyle: '', // 'beknopt' | 'gemiddeld' | 'uitgebreid' | ''
  industry: '',       // free-text industry / sector label
  companyUrl: '',     // raw user input (validated server-side at scrape time)
  companyContext: '', // text extracted from scrape, fed to LLM, not edited by user
  customRequirements: '', // free-text vacancy-specific notes
  voicePersona: DEFAULT_PERSONA_ID, // 'job' | 'jasmijn'
};

const PreferencesContext = createContext(null);

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

export function PreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(loadFromStorage);

  /** Has the user ever saved preferences? Used to auto-open the wizard once. */
  const hasConfigured = useMemo(
    () => Boolean(prefs.tone || prefs.paragraphStyle || prefs.industry || prefs.companyUrl || prefs.customRequirements),
    [prefs],
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Quota or private-mode, silently ignore; prefs stay in memory.
    }
  }, [prefs]);

  const updatePrefs = useCallback((partial) => {
    setPrefs(prev => ({ ...prev, ...partial }));
  }, []);

  const resetPrefs = useCallback(() => {
    setPrefs(DEFAULTS);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  /**
   * Build a Dutch system-prompt fragment from preferences. Returns null if no
   * field is set, caller can skip injection entirely. Consumed by
   * useStructuredChat to enrich every AI call with user-specific styling.
   */
  const buildPromptContext = useCallback(() => {
    const lines = [];
    if (prefs.tone) {
      const TONE_LABELS = {
        formeel: 'formeel en professioneel',
        informeel: 'informeel en toegankelijk',
        jong: 'jong, energiek en speels',
        zakelijk: 'zakelijk en to-the-point',
      };
      lines.push(`- Toon: ${TONE_LABELS[prefs.tone] || prefs.tone}`);
    }
    if (prefs.paragraphStyle) {
      const STYLE_LABELS = {
        beknopt: 'beknopt, gebruik bullet points en korte zinnen',
        gemiddeld: 'gemiddelde lengte, mix van bullets en lopende tekst',
        uitgebreid: 'uitgebreid, schrijf in volle lopende alineas',
      };
      lines.push(`- Stijl: ${STYLE_LABELS[prefs.paragraphStyle] || prefs.paragraphStyle}`);
    }
    if (prefs.industry) {
      lines.push(`- Branche: ${prefs.industry}`);
    }
    if (prefs.companyContext) {
      lines.push(`- Bedrijfscontext (van ${prefs.companyUrl || 'website'}):\n${prefs.companyContext.slice(0, 2500)}`);
    } else if (prefs.companyUrl) {
      lines.push(`- Bedrijfs-URL: ${prefs.companyUrl}`);
    }
    if (prefs.customRequirements) {
      lines.push(`- Specifieke wensen voor deze vacature: ${prefs.customRequirements}`);
    }
    if (lines.length === 0) return null;
    return `GEBRUIKERSVOORKEUREN, pas deze toon, stijl en context toe op alle gegenereerde vacaturetekst:\n${lines.join('\n')}`;
  }, [prefs]);

  /** Resolved persona object (with fallback if stored id is unknown). */
  const persona = useMemo(() => getPersona(prefs.voicePersona), [prefs.voicePersona]);

  /**
   * Sanity-check on every render: if user has selected a persona whose agent
   * isn't available (env var missing), silently fall back to the default.
   * Prevents starting a voice session with an undefined agentId.
   */
  useEffect(() => {
    const selected = PERSONAS[prefs.voicePersona];
    if (selected && !selected.available && prefs.voicePersona !== DEFAULT_PERSONA_ID) {
      setPrefs(p => ({ ...p, voicePersona: DEFAULT_PERSONA_ID }));
    }
  }, [prefs.voicePersona]);

  const value = useMemo(
    () => ({ prefs, updatePrefs, resetPrefs, hasConfigured, buildPromptContext, persona }),
    [prefs, updatePrefs, resetPrefs, hasConfigured, buildPromptContext, persona],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}
